/* All the logic surrounding bulk exports.

   There are three types:
   - Scenario export (files)
   - Composite maps
   - ROR (= even more composite maps)

   They are all created first, then scenarios can be added to them, then they run after
   which they are immutable.

 */

import { useQuery } from "@tanstack/react-query";

import {
  ApiBulkExport,
  BulkExportType,
  getBulkExport,
  getBulkExportCanStart,
  useDeleteBulkExport,
  useMultipleScenarios,
  useUpdateBulkExport,
} from "@nens/ldo/api-v1";
import { useTableData } from "@nens/react/hooks";
import { makeUnique } from "@nens/shared-utils";

import { useExportActionAllowed } from "./allowed-export-actions-hook";
import { isValidConfig } from "./domain";

interface BulkExportOptions {
  loadScenarios?: boolean;
}

export function useBulkExportHook(
  exportType: BulkExportType,
  exportId: number | null,
  options: BulkExportOptions
) {
  const loadScenarios = options.loadScenarios ?? true;
  const canStartResponse = useQuery(getBulkExportCanStart(exportId));
  const canStartLoaded = canStartResponse.isSuccess || canStartResponse.isError;
  const canStart = canStartResponse.isSuccess && canStartResponse.data.can_start;

  const bulkExportResponse = useQuery({
    ...getBulkExport(exportId),
    refetchInterval: 5 * 1000,
  });

  const bulkExport = bulkExportResponse.data ?? null;

  const pagination = useTableData({ initialPageSize: 10 });
  const numPages = Math.ceil((bulkExport?.scenario_ids.length ?? 0) / pagination.pageSize);

  const exportMutation = useUpdateBulkExport(bulkExport?.id ?? null);
  const deleteExportMutation = useDeleteBulkExport(bulkExport);

  const scenarioIds = (loadScenarios ? bulkExport?.scenario_ids ?? [] : []).slice(
    pagination.offset,
    pagination.offset + pagination.limit
  );
  const scenarioResponses = useMultipleScenarios(scenarioIds);
  const actionAllowed = useExportActionAllowed(bulkExportResponse.data ?? null);

  const anyScenarioError = scenarioResponses.some((response) => response.isError);

  const addScenarioId = (scenarioId: number) => {
    if (bulkExport === null || bulkExport.scenario_ids.includes(scenarioId)) return;
    const newScenarioIds = [...bulkExport.scenario_ids, scenarioId].sort();
    exportMutation.mutate({ scenario_ids: newScenarioIds });
  };

  const addScenarioIds = (scenarioIds: number[]) => {
    if (bulkExport === null) return;
    const newScenarioIds = makeUnique([...bulkExport.scenario_ids, ...scenarioIds]).sort();
    if (newScenarioIds.length === bulkExport.scenario_ids.length) return;
    exportMutation.mutate({ scenario_ids: newScenarioIds });
  };

  const removeScenarioId = (scenarioId: number) => {
    if (bulkExport === null || !bulkExport.scenario_ids.includes(scenarioId)) return;
    const newScenarioIds = bulkExport.scenario_ids.filter((id) => id !== scenarioId);
    exportMutation.mutate({ scenario_ids: newScenarioIds });
  };

  const submitExport = () => {
    if (bulkExport && actionAllowed("submit")) {
      exportMutation.mutate({ status: "submitted" });
    }
  };

  const archive = async () => {
    if (bulkExport && actionAllowed("archive")) {
      exportMutation.mutate({ status: "archived" });
    }
  };

  const share = async () => {
    if (bulkExport && actionAllowed("share")) {
      exportMutation.mutate({ status: "shared" });
    }
  };

  const unarchive = async () => {
    if (bulkExport && actionAllowed("unarchive")) {
      exportMutation.mutate({ status: "complete" });
    }
  };

  const formErrorKeys: string[] = bulkExport
    ? isValidConfig(exportType, bulkExport.name, bulkExport.config)
    : [];
  const formValid = formErrorKeys.length === 0 && actionAllowed("update");

  const updateConfig = async (config: ApiBulkExport["config"]) => {
    if (bulkExport && formValid) {
      exportMutation.mutate({ config });
    }
  };

  const editAllowed =
    (actionAllowed("create") || actionAllowed("update")) && !exportMutation.isPending;

  return {
    actionAllowed,
    bulkExportResponse,
    bulkExport,
    canStart,
    canStartLoaded,
    exportUpdating: exportMutation.isPending,
    addScenarioId,
    addScenarioIds,
    changeExportError: exportMutation.isError,
    removeScenarioId,
    scenarioIds,
    scenarioResponses,
    editAllowed,
    anyScenarioError,
    submitExport,
    archive,
    share,
    unarchive,
    deleteExportMutation,
    updateConfig,
    formErrorKeys,
    formValid,
    pagination,
    numPages,
  };
}
